import type { DictFragment } from '../translations';

/** Inventories & profile: People, Infrastructure, Deliverables pages + Profile. */
export const inventories: DictFragment = {
  fr: {
    // ── People ──
    'people.title': 'Personnes',
    'people.personOne': '{n} personne',
    'people.personMany': '{n} personnes',
    'people.addPerson': 'Ajouter une personne',
    'people.editPerson': 'Modifier la personne',
    'people.searchPlaceholder': 'Rechercher des personnes…',
    'people.emptyTitle': 'Aucune personne trouvée',
    'people.emptyDescSearch': 'Essayez d’ajuster votre recherche.',
    'people.emptyDescDefault': 'Ajoutez des personnes pour commencer.',
    'people.roles': 'Rôles',
    'people.projects': 'Projets',
    'people.visibility': 'Visibilité',
    'people.firstName': 'Prénom',
    'people.lastName': 'Nom',
    'people.firstNamePlaceholder': 'Prénom',
    'people.lastNamePlaceholder': 'Nom',
    'people.emailPlaceholder': 'email@exemple.com',
    'people.allProjects': 'Tous les projets',
    'people.involvedOnly': 'Impliqué uniquement',
    'people.visibilityAll': 'Tous',
    'people.visibilityInvolved': 'Impliqué',
    'people.account': 'Compte',
    'people.linked': 'Lié',
    'people.noAccount': 'Aucun compte',
    'people.projectsAsCollaborator': 'Projets en tant que collaborateur',
    'people.projectsAsSponsor': 'Projets en tant que commanditaire',
    'people.createUserAccount': 'Créer un compte utilisateur',
    'people.creating': 'Création…',
    'people.createUserConfirm': 'Créer un compte utilisateur pour {name} ? Cette personne pourra se connecter avec {email}.',
    'people.createAccount': 'Créer le compte',
    'people.userBadge': 'utilisateur',
    'people.firstNameRequired': 'Le prénom est requis',
    'people.saveChanges': 'Enregistrer les modifications',
    'people.deletePerson': 'Supprimer la personne',
    'people.deletePersonMessage': 'Supprimer cette personne ?',

    // ── Infrastructure ──
    'infra.title': 'Infrastructure',
    'infra.itemOne': '{n} élément',
    'infra.itemMany': '{n} éléments',
    'infra.newItemPlaceholder': 'Nouvel élément d’infrastructure…',
    'infra.searchPlaceholder': 'Rechercher…',
    'infra.emptyTitle': 'Aucun élément d’infrastructure',
    'infra.emptyDescSearch': 'Essayez d’ajuster votre recherche.',
    'infra.emptyDescDefault': 'Ajoutez des éléments d’infrastructure pour les suivre à travers les projets.',
    'infra.referencedBy': 'Référencé par des projets',
    'infra.noProjects': 'Aucun projet',
    'infra.deleteTitle': 'Supprimer l’élément d’infrastructure',
    'infra.deleteMessage': 'Supprimer cet élément d’infrastructure ?',

    // ── Deliverables ──
    'deliverables.title': 'Livrables',
    'deliverables.countOne': '{n} livrable dans tous les projets',
    'deliverables.countMany': '{n} livrables dans tous les projets',
    'deliverables.allCount': 'Tous ({n})',
    'deliverables.searchPlaceholder': 'Rechercher des livrables…',
    'deliverables.emptyTitle': 'Aucun livrable trouvé',
    'deliverables.emptyDescFilter': 'Essayez d’ajuster vos filtres.',
    'deliverables.emptyDescDefault': 'Les livrables sont ajoutés via les formulaires de projet.',
    'deliverables.collapse': 'Réduire',
    'deliverables.expand': 'Développer',
    'deliverables.bodyPlaceholder': 'Décrivez ce livrable…',

    // ── Profile ──
    'profile.tab.account': 'Compte',
    'profile.tab.permissions': 'Autorisations',
    'profile.tab.integrations': 'Intégrations',
    'profile.tab.notifications': 'Notifications',
    'profile.accountTitle': 'Compte',
    'profile.role': 'Rôle',
    'profile.roleAdmin': 'Admin',
    'profile.roleDirection': 'Direction',
    'profile.roleUser': 'Utilisateur',
    'profile.auth': 'Authentification',
    'profile.authOidc': 'Authentification unique (OIDC)',
    'profile.authPassword': 'Mot de passe',
    'profile.changePassword': 'Changer le mot de passe',
    'profile.currentPassword': 'Mot de passe actuel',
    'profile.newPassword': 'Nouveau mot de passe',
    'profile.pwPlaceholder': 'Au moins 8 caractères',
    'profile.confirmNewPassword': 'Confirmer le nouveau mot de passe',
    'profile.passwordChanged': 'Mot de passe modifié',
    'profile.pwMinLength': 'Le nouveau mot de passe doit comporter au moins 8 caractères',
    'profile.pwMismatch': 'Les mots de passe ne correspondent pas',
    'profile.pwChangeFailed': 'Échec du changement de mot de passe',
    'profile.permissionsTitle': 'Autorisations',
    'profile.permissionsIntro': 'Ce que votre rôle ({role}) peut faire dans le système.',
    'profile.capability': 'Fonctionnalité',
    'profile.calendarSync': 'Synchronisation du calendrier',
    'profile.calendarNotConfigured': 'L’intégration du calendrier n’a pas encore été configurée par un administrateur.',
    'profile.calendarInfoBefore': 'Les événements Outlook sont automatiquement synchronisés avec les échéanciers des projets. Ajoutez un code de projet (par ex. ',
    'profile.calendarInfoAfter': ') au titre d’une réunion pour la lier à ce projet.',
    'profile.outlookSessions': 'Copier mes séances de Mon calendrier dans mon calendrier Outlook',
    'profile.outlookSessionsHelp': 'Chaque séance planifiée crée un rendez-vous dans votre calendrier Outlook (titre « t-ae14 … », détails de la tâche), mis à jour quand vous la déplacez et supprimé quand vous la retirez ; supprimer le rendez-vous dans Outlook retire aussi la séance. Désactiver retire les rendez-vous créés par PMO, les séances restent.',
    'profile.active': 'Actif',
    'profile.speakrTitle': 'Speakr (Transcriptions)',
    'profile.speakrNotConfigured': 'L’URL de base Speakr n’a pas encore été configurée par un administrateur.',
    'profile.speakrIntro': 'Connectez votre compte Speakr pour récupérer les transcriptions de réunions. Votre jeton d’API est personnel et stocké de manière sécurisée.',
    'profile.speakrUrl': 'URL Speakr',
    'profile.speakrUrlHint': 'Laissez vide pour utiliser la valeur par défaut : {value}',
    'profile.notSet': '(non défini)',
    'profile.apiToken': 'Jeton d’API',
    'profile.apiTokenPlaceholder': 'Collez votre jeton d’API Speakr',
    'profile.tokenSet': 'Jeton défini',
    'profile.syncing': 'Synchronisation…',
    'profile.syncNow': 'Synchroniser maintenant',
    'profile.syncedOne': '{n} transcription synchronisée',
    'profile.syncedMany': '{n} transcriptions synchronisées',
    'profile.syncFailed': 'Échec de la synchronisation',
    'profile.clearToken': 'Effacer le jeton',
    'profile.notifPrefsTitle': 'Préférences de notification',
    'profile.notifPrefsIntro': 'Choisissez les notifications que vous recevez. Les modifications sont enregistrées automatiquement.',
    'profile.notifAllProjects': 'Tous les projets',
    'profile.notifSponsoredOnly': 'Uniquement les projets que je commandite',
    'profile.notifType.mention': 'Quand quelqu’un me mentionne (@)',
    'profile.notifType.mention.desc': 'Être notifié quand vous êtes tagué dans un commentaire',
    'profile.notifType.email_mention': 'E-mail quand on me mentionne',
    'profile.notifType.email_mention.desc': 'Recevoir un e-mail quand quelqu’un vous mentionne (@) dans un commentaire sur une tâche ou un élément de to-do list',
    'profile.notifType.email_task_assigned': 'E-mail quand on me rattache à une tâche',
    'profile.notifType.email_task_assigned.desc': 'Recevoir un e-mail quand quelqu’un vous désigne responsable ou collaborateur·rice d’une tâche',
    'profile.notifType.email_project_involvement': 'E-mail quand on me rattache à un projet ou à un jalon',
    'profile.notifType.email_project_involvement.desc': 'Recevoir un e-mail quand vous devenez sponsor, accompagnant·e PMO, collaborateur·rice ou membre de l’équipe d’un projet, ou quand vous êtes nommé·e sur un de ses jalons',
    'profile.notifType.task_contact': 'Quand on me contacte au sujet d’une tâche que j’ai créée',
    'profile.notifType.task_contact.desc': 'Être notifié dans l’application quand quelqu’un vous pose une question sur une de vos tâches',
    'profile.notifType.email_task_contact': 'E-mail quand on me contacte au sujet d’une tâche que j’ai créée',
    'profile.notifType.email_task_contact.desc': 'Recevoir un e-mail quand quelqu’un vous pose une question sur une de vos tâches',
    'profile.notifType.project_status_changed': 'Quand un projet change de statut',
    'profile.notifType.project_status_changed.desc': 'Être notifié quand des projets avancent dans le pipeline',
    'profile.notifType.project_description_changed': 'Quand la configuration d’un projet change',
    'profile.notifType.project_description_changed.desc': 'Être notifié quand un projet que vous commanditez est modifié',
    'profile.notifType.task_created_in_project': 'Quand une tâche est créée dans un projet que je commandite',
    'profile.notifType.task_created_in_project.desc': 'Être notifié des nouvelles tâches dans vos projets',
    'profile.notifType.task_status_changed_in_project': 'Quand une tâche change de statut dans un projet que je commandite',
    'profile.notifType.task_status_changed_in_project.desc': 'Être notifié quand des tâches avancent dans vos projets',
    'profile.notifType.project_comment': 'Quand quelqu’un commente un projet que je commandite',
    'profile.notifType.project_comment.desc': 'Être notifié des commentaires sur vos projets',
  },
  en: {
    // ── People ──
    'people.title': 'People',
    'people.personOne': '{n} person',
    'people.personMany': '{n} people',
    'people.addPerson': 'Add Person',
    'people.editPerson': 'Edit Person',
    'people.searchPlaceholder': 'Search people...',
    'people.emptyTitle': 'No people found',
    'people.emptyDescSearch': 'Try adjusting your search.',
    'people.emptyDescDefault': 'Add people to get started.',
    'people.roles': 'Roles',
    'people.projects': 'Projects',
    'people.visibility': 'Visibility',
    'people.firstName': 'First Name',
    'people.lastName': 'Last Name',
    'people.firstNamePlaceholder': 'First name',
    'people.lastNamePlaceholder': 'Last name',
    'people.emailPlaceholder': 'email@example.com',
    'people.allProjects': 'All Projects',
    'people.involvedOnly': 'Involved Only',
    'people.visibilityAll': 'All',
    'people.visibilityInvolved': 'Involved',
    'people.account': 'Account',
    'people.linked': 'Linked',
    'people.noAccount': 'No account',
    'people.projectsAsCollaborator': 'Projects as Collaborator',
    'people.projectsAsSponsor': 'Projects as Sponsor',
    'people.createUserAccount': 'Create User Account',
    'people.creating': 'Creating...',
    'people.createUserConfirm': 'Create a user account for {name}? They will be able to log in with {email}.',
    'people.createAccount': 'Create Account',
    'people.userBadge': 'user',
    'people.firstNameRequired': 'First name is required',
    'people.saveChanges': 'Save Changes',
    'people.deletePerson': 'Delete Person',
    'people.deletePersonMessage': 'Delete this person?',

    // ── Infrastructure ──
    'infra.title': 'Infrastructure',
    'infra.itemOne': '{n} item',
    'infra.itemMany': '{n} items',
    'infra.newItemPlaceholder': 'New infrastructure item...',
    'infra.searchPlaceholder': 'Search...',
    'infra.emptyTitle': 'No infrastructure items',
    'infra.emptyDescSearch': 'Try adjusting your search.',
    'infra.emptyDescDefault': 'Add infrastructure items to track them across projects.',
    'infra.referencedBy': 'Referenced by Projects',
    'infra.noProjects': 'No projects',
    'infra.deleteTitle': 'Delete Infrastructure Item',
    'infra.deleteMessage': 'Delete this infrastructure item?',

    // ── Deliverables ──
    'deliverables.title': 'Deliverables',
    'deliverables.countOne': '{n} deliverable across all projects',
    'deliverables.countMany': '{n} deliverables across all projects',
    'deliverables.allCount': 'All ({n})',
    'deliverables.searchPlaceholder': 'Search deliverables...',
    'deliverables.emptyTitle': 'No deliverables found',
    'deliverables.emptyDescFilter': 'Try adjusting your filters.',
    'deliverables.emptyDescDefault': 'Deliverables are added through project forms.',
    'deliverables.collapse': 'Collapse',
    'deliverables.expand': 'Expand',
    'deliverables.bodyPlaceholder': 'Describe this deliverable…',

    // ── Profile ──
    'profile.tab.account': 'Account',
    'profile.tab.permissions': 'Permissions',
    'profile.tab.integrations': 'Integrations',
    'profile.tab.notifications': 'Notifications',
    'profile.accountTitle': 'Account',
    'profile.role': 'Role',
    'profile.roleAdmin': 'Admin',
    'profile.roleDirection': 'Direction',
    'profile.roleUser': 'User',
    'profile.auth': 'Auth',
    'profile.authOidc': 'Single Sign-On (OIDC)',
    'profile.authPassword': 'Password',
    'profile.changePassword': 'Change Password',
    'profile.currentPassword': 'Current Password',
    'profile.newPassword': 'New Password',
    'profile.pwPlaceholder': 'At least 8 characters',
    'profile.confirmNewPassword': 'Confirm New Password',
    'profile.passwordChanged': 'Password Changed',
    'profile.pwMinLength': 'New password must be at least 8 characters',
    'profile.pwMismatch': 'Passwords do not match',
    'profile.pwChangeFailed': 'Failed to change password',
    'profile.permissionsTitle': 'Permissions',
    'profile.permissionsIntro': 'What your role ({role}) can do in the system.',
    'profile.capability': 'Capability',
    'profile.calendarSync': 'Calendar Sync',
    'profile.calendarNotConfigured': 'Calendar integration has not been configured by an admin yet.',
    'profile.calendarInfoBefore': 'Outlook events are automatically synced to project timelines. Add a project code (e.g. ',
    'profile.calendarInfoAfter': ') to a meeting title to link it to that project.',
    'profile.outlookSessions': 'Copy my My-calendar sessions into my Outlook calendar',
    'profile.outlookSessionsHelp': 'Each planned session creates an appointment in your Outlook calendar (title “t-ae14 …”, the task’s details), updated when you move it and removed when you remove it; deleting the appointment in Outlook removes the session too. Turning this off removes the appointments PMO created; the sessions stay.',
    'profile.active': 'Active',
    'profile.speakrTitle': 'Speakr (Transcriptions)',
    'profile.speakrNotConfigured': 'Speakr base URL has not been configured by an admin yet.',
    'profile.speakrIntro': 'Connect your Speakr account to pull meeting transcriptions. Your API token is personal and stored securely.',
    'profile.speakrUrl': 'Speakr URL',
    'profile.speakrUrlHint': 'Leave empty to use the default: {value}',
    'profile.notSet': '(not set)',
    'profile.apiToken': 'API Token',
    'profile.apiTokenPlaceholder': 'Paste your Speakr API token',
    'profile.tokenSet': 'Token Set',
    'profile.syncing': 'Syncing…',
    'profile.syncNow': 'Sync now',
    'profile.syncedOne': 'Synced {n} transcript',
    'profile.syncedMany': 'Synced {n} transcripts',
    'profile.syncFailed': 'Sync failed',
    'profile.clearToken': 'Clear Token',
    'profile.notifPrefsTitle': 'Notification Preferences',
    'profile.notifPrefsIntro': 'Choose which notifications you receive. Changes are saved automatically.',
    'profile.notifAllProjects': 'All projects',
    'profile.notifSponsoredOnly': 'Only projects I sponsor',
    'profile.notifType.mention': 'When someone @-mentions me',
    'profile.notifType.mention.desc': 'Get notified when you are tagged in a comment',
    'profile.notifType.email_mention': 'E-mail when someone @-mentions me',
    'profile.notifType.email_mention.desc': 'Receive an e-mail when you are tagged in a comment on a task or a to-do item',
    'profile.notifType.email_task_assigned': 'E-mail when I am attached to a task',
    'profile.notifType.email_task_assigned.desc': 'Receive an e-mail when someone makes you responsible for, or a collaborator on, a task',
    'profile.notifType.email_project_involvement': 'E-mail when I am attached to a project or a milestone',
    'profile.notifType.email_project_involvement.desc': 'Receive an e-mail when you become sponsor, PMO accompanist, collaborator or team member of a project, or are named on one of its milestones',
    'profile.notifType.task_contact': 'When someone contacts me about a task I created',
    'profile.notifType.task_contact.desc': 'Get notified in the app when someone asks you about one of your tasks',
    'profile.notifType.email_task_contact': 'E-mail when someone contacts me about a task I created',
    'profile.notifType.email_task_contact.desc': 'Receive an e-mail when someone asks you about one of your tasks',
    'profile.notifType.project_status_changed': 'When a project changes status',
    'profile.notifType.project_status_changed.desc': 'Get notified when projects move through the pipeline',
    'profile.notifType.project_description_changed': 'When a project’s setup is changed',
    'profile.notifType.project_description_changed.desc': 'Get notified when a project you sponsor is modified',
    'profile.notifType.task_created_in_project': 'When a new task is created in my sponsored project',
    'profile.notifType.task_created_in_project.desc': 'Get notified about new tasks in projects you sponsor',
    'profile.notifType.task_status_changed_in_project': 'When a task status changes in my sponsored project',
    'profile.notifType.task_status_changed_in_project.desc': 'Get notified when tasks move in projects you sponsor',
    'profile.notifType.project_comment': 'When someone comments on my sponsored project',
    'profile.notifType.project_comment.desc': 'Get notified about comments on projects you sponsor',
  },
};
